/**
 * Central language catalog and helpers
 * - Store language codes and names in one place
 * - Distinguish which languages are supported as native vs target
 * - Make it easy to add/enable more languages in the future
 */

/**
 * Each language entry describes the code, names, and where it's supported.
 * - code: BCP-47 or ISO 639-1 language code
 * - name: English display name for UI
 * - nativeName: Autonym for future i18n-friendly displays
 * - dir: text direction (ltr/rtl)
 * - supportedAs: which contexts the language is currently supported for
 * - enabled: feature flag to quickly turn a language on/off globally
 */
export const LANGUAGE_CATALOG = Object.freeze({
  en: {
    code: "en",
    name: "English",
    nativeName: "English",
    bcp47: "en",
    dir: "ltr",
    supportedAs: { native: true, target: false },
    enabled: true,
  },
  es: {
    code: "es",
    name: "Spanish",
    nativeName: "Español",
    bcp47: "es",
    dir: "ltr",
    supportedAs: { native: false, target: true },
    enabled: true,
  },
  fr: {
    code: "fr",
    name: "French",
    nativeName: "Français",
    bcp47: "fr",
    dir: "ltr",
    supportedAs: { native: false, target: true },
    enabled: true,
  },
  it: {
    code: "it",
    name: "Italian",
    nativeName: "Italiano",
    bcp47: "it",
    dir: "ltr",
    supportedAs: { native: false, target: true },
    enabled: true,
  },
  zh: {
    code: "zh",
    name: "Mandarin",
    nativeName: "中文",
    bcp47: "zh",
    dir: "ltr",
    supportedAs: { native: false, target: true },
    enabled: true,
  },
  // Example for future additions:
  // de: {
  //   code: "de",
  //   name: "German",
  //   nativeName: "Deutsch",
  //   bcp47: "de",
  //   dir: "ltr",
  //   supportedAs: { native: false, target: true },
  //   enabled: false, // flip to true when fully supported
  // },
});

/**
 * Get catalog entry by code
 * @param {string} code
 * @returns {object|undefined}
 */
export function getLanguage(code) {
  return LANGUAGE_CATALOG[code];
}

/**
 * Get display name for a code. Falls back to the code itself.
 * @param {string} code
 * @returns {string}
 */
export function nameFor(code) {
  return LANGUAGE_CATALOG[code]?.name || code;
}

/**
 * Is this code currently supported as a native language?
 * @param {string} code
 * @returns {boolean}
 */
export function isSupportedNativeLanguage(code) {
  const lang = LANGUAGE_CATALOG[code];
  return Boolean(lang && lang.enabled && lang.supportedAs?.native);
}

/**
 * Is this code currently supported as a target language?
 * @param {string} code
 * @returns {boolean}
 */
export function isSupportedTargetLanguage(code) {
  const lang = LANGUAGE_CATALOG[code];
  return Boolean(lang && lang.enabled && lang.supportedAs?.target);
}

/**
 * List languages filtered by a predicate and sorted by name
 * @param {(entry: object) => boolean} predicate
 * @returns {{code: string, name: string}[]}
 */
function listLanguages(predicate) {
  return Object.values(LANGUAGE_CATALOG)
    .filter((l) => l.enabled && predicate(l))
    .map((l) => ({ code: l.code, name: l.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * List languages supported as native (for onboarding native selection)
 * Currently: English only
 * @returns {{code: string, name: string}[]}
 */
export function listNativeLanguages() {
  return listLanguages((l) => l.supportedAs?.native);
}

/**
 * List languages supported as target (for onboarding target selection)
 * Currently: es, fr, it, zh
 * @returns {{code: string, name: string}[]}
 */
export function listTargetLanguages() {
  return listLanguages((l) => l.supportedAs?.target);
}

/**
 * Utility to validate that a provided native language code is supported.
 * Throws an Error with a clear message if invalid.
 * @param {string} code
 */
export function assertValidNativeLanguage(code) {
  if (!isSupportedNativeLanguage(code)) {
    const allowed = listNativeLanguages()
      .map((l) => l.code)
      .join(", ");
    throw new Error(
      `Unsupported native language code "${code}". Allowed: ${allowed}`
    );
  }
}

/**
 * Utility to validate that a provided target (learning) language code is supported.
 * Throws an Error with a clear message if invalid.
 * @param {string} code
 */
export function assertValidTargetLanguage(code) {
  if (!isSupportedTargetLanguage(code)) {
    const allowed = listTargetLanguages()
      .map((l) => l.code)
      .join(", ");
    throw new Error(
      `Unsupported target language code "${code}". Allowed: ${allowed}`
    );
  }
}
